import { useState, useEffect, useRef } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Folder, FolderOpen, Check, Loader2, Edit2, Trash2, Plus, X, Save } from "lucide-react";
import { cn } from "@/lib/utils";

interface FolderPopoverProps {
  patientId: string;
  selectedFolder: string | null;
  onSelectFolder: (folder: string | null) => void;
  onFoldersChanged?: () => void;
}

export function FolderPopover({
  patientId,
  selectedFolder,
  onSelectFolder,
  onFoldersChanged,
}: FolderPopoverProps) {
  const [folders, setFolders] = useState<Array<{ name: string; count: number }>>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingFolder, setEditingFolder] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [showCreateInput, setShowCreateInput] = useState(false);
  const [folderToDelete, setFolderToDelete] = useState<string | null>(null);
  const [folderToDeleteImageCount, setFolderToDeleteImageCount] = useState<number>(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const editInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      loadFolders();
    }
  }, [open, patientId]);

  useEffect(() => {
    if (showCreateInput && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showCreateInput]);

  useEffect(() => {
    if (editingFolder && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingFolder]);

  async function loadFolders() {
    try {
      setLoading(true);
      // Buscar todas as pastas da tabela de pastas
      // @ts-ignore - patient_gallery_folders table not yet in types
      const { data: foldersData, error: foldersError } = await supabase
        .from("patient_gallery_folders")
        .select("name")
        .eq("patient_id", patientId)
        .order("name", { ascending: true });

      if (foldersError) throw foldersError;

      // Buscar todas as imagens para contar por pasta
      // @ts-ignore - patient_gallery table not yet in types
      const { data: imagesData, error: imagesError } = await supabase
        .from("patient_gallery")
        .select("folder")
        .eq("patient_id", patientId);

      if (imagesError) throw imagesError;

      // Contar imagens por pasta
      const folderCounts: Record<string, number> = {};
      (imagesData || []).forEach((item) => {
        const folder = item.folder || null;
        if (folder) {
          folderCounts[folder] = (folderCounts[folder] || 0) + 1;
        }
      });

      // Criar array com todas as pastas (mesmo as vazias) e suas contagens
      const foldersArray = (foldersData || []).map((folder) => ({
        name: folder.name,
        count: folderCounts[folder.name] || 0,
      }));

      setFolders(foldersArray);
    } catch (error) {
      console.error("Erro ao carregar pastas:", error);
    } finally {
      setLoading(false);
    }
  }

  function handleSelectFolder(folder: string | null) {
    onSelectFolder(folder);
    setOpen(false);
  }

  async function createFolder() {
    if (!newFolderName.trim()) {
      toast.error("Digite um nome para a pasta");
      return;
    }

    const folderName = newFolderName.trim();

    // Verificar se já existe
    if (folders.some(f => f.name === folderName)) {
      toast.error("Já existe uma pasta com este nome");
      return;
    }

    try {
      setIsCreatingFolder(true);

      // Obter usuário autenticado
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error("Usuário não autenticado");
        return;
      }

      // Criar pasta no banco de dados
      const { error } = await supabase
        .from("patient_gallery_folders")
        .insert({
          patient_id: patientId,
          owner_id: user.id,
          name: folderName,
        });

      if (error) {
        if (error.code === "23505") {
          toast.error("Já existe uma pasta com este nome");
        } else {
          throw error;
        }
        return;
      }

      await loadFolders();
      setNewFolderName("");
      setShowCreateInput(false);
      toast.success("Pasta criada!");
      onFoldersChanged?.();
    } catch (error) {
      console.error("Erro ao criar pasta:", error);
      toast.error("Erro ao criar pasta");
    } finally {
      setIsCreatingFolder(false);
    }
  }

  async function renameFolder(oldName: string, newName: string) {
    if (!newName.trim()) {
      toast.error("Digite um nome para a pasta");
      return;
    }

    const trimmedName = newName.trim();

    // Verificar se já existe
    if (folders.some(f => f.name === trimmedName && f.name !== oldName)) {
      toast.error("Já existe uma pasta com este nome");
      return;
    }

    try {
      setIsSaving(true);

      // Atualizar nome da pasta na tabela de pastas
      const { error: folderError } = await supabase
        .from("patient_gallery_folders")
        .update({ name: trimmedName })
        .eq("patient_id", patientId)
        .eq("name", oldName);

      if (folderError) {
        if (folderError.code === "23505") {
          toast.error("Já existe uma pasta com este nome");
        } else {
          throw folderError;
        }
        return;
      }

      // Atualizar todas as imagens que estão nesta pasta
      const { error: galleryError } = await supabase
        .from("patient_gallery")
        .update({ folder: trimmedName })
        .eq("patient_id", patientId)
        .eq("folder", oldName);

      if (galleryError) throw galleryError;

      // Se a pasta renomeada era a selecionada, atualizar seleção
      if (selectedFolder === oldName) {
        onSelectFolder(trimmedName);
      }

      await loadFolders();
      setEditingFolder(null);
      setEditingName("");
      toast.success("Pasta renomeada!");
      onFoldersChanged?.();
    } catch (error) {
      console.error("Erro ao renomear pasta:", error);
      toast.error("Erro ao renomear pasta");
    } finally {
      setIsSaving(false);
    }
  }

  async function deleteFolder(folderName: string) {
    try {
      setIsDeleting(true);

      // Buscar todas as imagens desta pasta
      const { data: images, error: fetchError } = await supabase
        .from("patient_gallery")
        .select("id, file_path")
        .eq("patient_id", patientId)
        .eq("folder", folderName);

      if (fetchError) throw fetchError;

      // Deletar arquivos do storage
      if (images && images.length > 0) {
        const filePaths = images.map((img) => img.file_path);
        const { error: storageError } = await supabase.storage
          .from("patient-docs")
          .remove(filePaths);

        if (storageError) {
          console.error("Erro ao deletar arquivos do storage:", storageError);
        }
      }

      // Deletar registros das imagens do banco
      if (images && images.length > 0) {
        const imageIds = images.map((img) => img.id);
        const { error: deleteImagesError } = await supabase
          .from("patient_gallery")
          .delete()
          .in("id", imageIds);

        if (deleteImagesError) throw deleteImagesError;
      }

      // Deletar pasta da tabela de pastas
      const { error: folderError } = await supabase
        .from("patient_gallery_folders")
        .delete()
        .eq("patient_id", patientId)
        .eq("name", folderName);

      if (folderError) throw folderError;

      // Se a pasta deletada era a selecionada, limpar seleção
      if (selectedFolder === folderName) {
        onSelectFolder(null);
      }

      await loadFolders();
      setFolderToDelete(null);
      setFolderToDeleteImageCount(0);
      
      const imageCount = images?.length || 0;
      if (imageCount > 0) {
        toast.success(`Pasta deletada! ${imageCount} imagem(ns) foram excluída(s) permanentemente.`);
      } else {
        toast.success("Pasta deletada!");
      }
      onFoldersChanged?.();
    } catch (error) {
      console.error("Erro ao deletar pasta:", error);
      toast.error("Erro ao deletar pasta");
    } finally {
      setIsDeleting(false);
    }
  }

  function startEdit(folderName: string) {
    setEditingFolder(folderName);
    setEditingName(folderName);
  }

  function cancelEdit() {
    setEditingFolder(null);
    setEditingName("");
  }

  async function handleDeleteClick(folderName: string) {
    // Buscar contagem de imagens na pasta
    const { data: images, error } = await supabase
      .from("patient_gallery")
      .select("id")
      .eq("patient_id", patientId)
      .eq("folder", folderName);
    
    if (!error) {
      setFolderToDeleteImageCount(images?.length || 0);
    }
    setFolderToDelete(folderName);
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="flex items-center gap-2">
          {selectedFolder ? (
            <>
              <Folder className="h-4 w-4" />
              <span className="max-w-[150px] truncate">{selectedFolder}</span>
            </>
          ) : (
            <>
              <FolderOpen className="h-4 w-4" />
              <span>Selecionar Pasta</span>
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        <div className="p-2">
          <div className="px-2 py-1.5 text-sm font-semibold text-muted-foreground">
            Selecionar Pasta
          </div>
          
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="space-y-1">
              {/* Opção "Todas as fotos" */}
              <button
                onClick={() => handleSelectFolder(null)}
                className={cn(
                  "w-full flex items-center justify-between px-2 py-2 rounded-md text-sm transition-colors hover:bg-accent",
                  selectedFolder === null && "bg-accent"
                )}
              >
                <div className="flex items-center gap-2">
                  <FolderOpen className="h-4 w-4" />
                  <span>Todas as fotos</span>
                </div>
                {selectedFolder === null && (
                  <Check className="h-4 w-4 text-primary" />
                )}
              </button>

              {/* Lista de pastas */}
              {folders.length === 0 ? (
                <div className="px-2 py-4 text-center text-sm text-muted-foreground">
                  Nenhuma pasta criada ainda
                </div>
              ) : (
                folders.map((folder) => (
                  <div
                    key={folder.name}
                    className="group flex items-center gap-1"
                  >
                    {editingFolder === folder.name ? (
                      <div className="flex items-center gap-1 flex-1 px-2 py-2">
                        <Input
                          ref={editInputRef}
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              renameFolder(folder.name, editingName);
                            } else if (e.key === "Escape") {
                              cancelEdit();
                            }
                          }}
                          className="h-7 text-sm flex-1"
                          disabled={isSaving}
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={cancelEdit}
                          disabled={isSaving}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => renameFolder(folder.name, editingName)}
                          disabled={isSaving || !editingName.trim()}
                        >
                          {isSaving ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Save className="h-3 w-3" />
                          )}
                        </Button>
                      </div>
                    ) : (
                      <>
                        <button
                          onClick={() => handleSelectFolder(folder.name)}
                          className={cn(
                            "flex items-center gap-2 flex-1 min-w-0 px-2 py-2 rounded-md text-sm transition-colors hover:bg-accent",
                            selectedFolder === folder.name && "bg-accent"
                          )}
                        >
                          <Folder className="h-4 w-4 flex-shrink-0" />
                          <span className="truncate">{folder.name}</span>
                          <Badge variant="secondary" className="ml-auto text-xs">
                            {folder.count}
                          </Badge>
                          {selectedFolder === folder.name && (
                            <Check className="h-4 w-4 text-primary ml-2 flex-shrink-0" />
                          )}
                        </button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={(e) => {
                            e.stopPropagation();
                            startEdit(folder.name);
                          }}
                        >
                          <Edit2 className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity text-destructive hover:text-destructive"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteClick(folder.name);
                          }}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </>
                    )}
                  </div>
                ))
              )}

              {/* Input para criar nova pasta */}
              {showCreateInput ? (
                <div className="px-2 py-2 space-y-2 border-t mt-1">
                  <div className="flex items-center gap-2">
                    <Input
                      ref={inputRef}
                      value={newFolderName}
                      onChange={(e) => setNewFolderName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          createFolder();
                        } else if (e.key === "Escape") {
                          setNewFolderName("");
                          setShowCreateInput(false);
                        }
                      }}
                      placeholder="Nome da nova pasta"
                      className="h-8 text-sm"
                      disabled={isCreatingFolder}
                    />
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8"
                      onClick={() => {
                        setNewFolderName("");
                        setShowCreateInput(false);
                      }}
                      disabled={isCreatingFolder}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8"
                      onClick={createFolder}
                      disabled={isCreatingFolder || !newFolderName.trim()}
                    >
                      {isCreatingFolder ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Save className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setShowCreateInput(true)}
                  className="w-full flex items-center gap-2 px-2 py-2 rounded-md text-sm transition-colors hover:bg-accent text-muted-foreground border-t mt-1 pt-2"
                >
                  <Plus className="h-4 w-4" />
                  <span>Adicionar pasta</span>
                </button>
              )}
            </div>
          )}
        </div>
      </PopoverContent>

      {/* Dialog de confirmação de exclusão */}
      <AlertDialog
        open={folderToDelete !== null}
        onOpenChange={(open) => {
          if (!open) {
            setFolderToDelete(null);
            setFolderToDeleteImageCount(0);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>⚠️ Confirmar Exclusão da Pasta</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>
                  Tem certeza que deseja deletar a pasta <strong>"{folderToDelete}"</strong>?
                </p>
                {folderToDeleteImageCount > 0 ? (
                  <div className="bg-destructive/10 border border-destructive/20 rounded-md p-3 mt-3">
                    <p className="text-destructive font-semibold">
                      ⚠️ ATENÇÃO: Esta ação irá deletar permanentemente {folderToDeleteImageCount} imagem(ns) que estão nesta pasta!
                    </p>
                    <p className="text-sm text-destructive/80 mt-1">
                      As imagens serão excluídas do storage e do banco de dados. Esta ação não pode ser desfeita.
                    </p>
                  </div>
                ) : (
                  <p className="text-muted-foreground">
                    A pasta está vazia e será deletada permanentemente.
                  </p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => folderToDelete && deleteFolder(folderToDelete)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deletando...
                </>
              ) : (
                "Sim, deletar pasta e imagens"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Popover>
  );
}

